import * as THREE from "three";

/**
 * S11.8.3 — helper thuần của viewer 3D: cap DPR, vòng đời tài nguyên `three`, vòng lặp RAF và
 * `prefers-reduced-motion`.
 *
 * Module này KHÔNG chạm React, DOM canvas hay WebGL: mọi hàm nhận object `three` thuần nên
 * chạy được trong jsdom (vốn không có context WebGL). Nhờ đó đây là nơi duy nhất của viewer
 * khẳng định trực tiếp việc dispose geometry/material/texture và việc dừng RAF sạch.
 *
 * Toàn bộ module là page-local của feature `cemetery-3d`: không biết route, auth hay manifest.
 */

/** Trạng thái camera scene-local (không phải toạ độ GIS) mà viewer ghi qua mutable ref. */
export type SceneCameraState = Readonly<{
  position: readonly [number, number, number];
  target: readonly [number, number, number];
}>;

/** Vòng lặp RAF dừng sạch được; `onFrame` nhận timestamp của chính frame đang chạy. */
export type FrameLoop = Readonly<{
  start: () => void;
  stop: () => void;
  isRunning: () => boolean;
}>;

/** Sàn DPR: dưới 1 thì nét hơn nhưng tốn pixel vô ích, nên luôn kéo về tối thiểu 1. */
const MIN_DEVICE_PIXEL_RATIO = 1;
/** Trần DPR theo Plan Global Constraints (S11.8): không vượt 2 kể cả màn hình rất nét. */
const MAX_DEVICE_PIXEL_RATIO = 2;
/** Nhịp thay thế khi môi trường không có `requestAnimationFrame` (SSR/worker). */
const FALLBACK_FRAME_MS = 16;

/**
 * Chuẩn hoá device pixel ratio vào khoảng [1, 2] và **không** làm tròn, để màn hình 1.5x vẫn
 * giữ đúng tỉ lệ. Giá trị không hữu hạn (NaN, Infinity, undefined) rơi về biên an toàn.
 */
export function resolveDevicePixelRatio(candidate?: number): number {
  const requested =
    candidate ?? (typeof window === "undefined" ? undefined : window.devicePixelRatio);
  if (typeof requested !== "number" || !Number.isFinite(requested)) {
    return MIN_DEVICE_PIXEL_RATIO;
  }
  if (requested <= MIN_DEVICE_PIXEL_RATIO) return MIN_DEVICE_PIXEL_RATIO;
  if (requested >= MAX_DEVICE_PIXEL_RATIO) return MAX_DEVICE_PIXEL_RATIO;
  return requested;
}

/**
 * Sổ đăng ký tài nguyên đã giải phóng. Dùng `WeakSet` để (a) không giữ object sống thêm và
 * (b) chịu được double-invoke của StrictMode cũng như nhiều mesh dùng chung một geometry/material.
 */
const disposedResources = new WeakSet<object>();

function disposeOnce(resource: { dispose: () => void }): void {
  if (disposedResources.has(resource)) return;
  disposedResources.add(resource);
  resource.dispose();
}

/**
 * Giải phóng tài nguyên GPU của một cây `three`: geometry, material (kể cả mảng material của
 * mesh nhiều chất liệu) và mọi texture gắn qua material (`map`, `normalMap`, ...).
 *
 * Idempotent: gọi lại nhiều lượt vẫn chỉ dispose mỗi tài nguyên đúng một lần.
 */
export function disposeSceneResources(root: THREE.Object3D): void {
  root.traverse((node) => {
    const renderable = node as unknown as Partial<THREE.Mesh>;
    const geometry = renderable.geometry;
    if (geometry instanceof THREE.BufferGeometry) disposeOnce(geometry);

    const material = renderable.material;
    if (Array.isArray(material)) {
      for (const entry of material) disposeMaterial(entry);
    } else if (material instanceof THREE.Material) {
      disposeMaterial(material);
    }
  });
}

/** Dispose texture gắn qua material trước, rồi tới chính material. */
function disposeMaterial(material: THREE.Material): void {
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) disposeOnce(value);
  }
  disposeOnce(material);
}

/**
 * Người dùng có bật giảm chuyển động không. Thiếu `matchMedia` (môi trường test/SSR cũ) trả
 * `false` thay vì ném lỗi: viewer phải chạy được ở mọi môi trường.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}

function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

/**
 * Vòng lặp RAF tất định: mỗi frame chạy `onFrame` đúng một lần rồi tự xếp frame kế tiếp,
 * `stop()` hủy frame đang chờ và `start()` lặp lại không nhân đôi callback.
 *
 * `requestAnimationFrame`/`cancelAnimationFrame` được đọc **tại thời điểm gọi**, không cache ở
 * module scope: môi trường test hoặc polyfill có thể thay chúng giữa hai lượt chạy.
 */
export function createFrameLoop(onFrame: (timestampMs: number) => void): FrameLoop {
  let running = false;
  let pending: { readonly id: number; readonly viaTimer: boolean } | null = null;

  const schedule = (): void => {
    const requestFrame = globalThis.requestAnimationFrame;
    if (typeof requestFrame === "function") {
      const id = requestFrame.call(globalThis, (timestamp: number) => {
        pending = null;
        tick(timestamp);
      });
      pending = { id, viaTimer: false };
      return;
    }
    const id = globalThis.setTimeout(() => {
      pending = null;
      tick(nowMs());
    }, FALLBACK_FRAME_MS) as unknown as number;
    pending = { id, viaTimer: true };
  };

  const cancelPending = (): void => {
    if (pending === null) return;
    const { id, viaTimer } = pending;
    pending = null;
    if (viaTimer) {
      globalThis.clearTimeout(id);
      return;
    }
    const cancelFrame = globalThis.cancelAnimationFrame;
    if (typeof cancelFrame === "function") cancelFrame.call(globalThis, id);
  };

  const tick = (timestamp: number): void => {
    if (!running) return;
    try {
      onFrame(timestamp);
    } finally {
      // `onFrame` có thể gọi `stop()`; chỉ xếp frame mới khi vòng lặp còn chạy.
      if (running && pending === null) schedule();
    }
  };

  return {
    start: (): void => {
      if (running) return;
      running = true;
      schedule();
    },
    stop: (): void => {
      running = false;
      cancelPending();
    },
    isRunning: (): boolean => running,
  };
}
